import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Divider,
  TextField,
  CircularProgress,
  Paper,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import LockIcon from '@mui/icons-material/Lock';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { BOOKING_THEME } from '../bookingTheme';
import { formatTimeWithZone } from '../../../utils/timezone';
import type { BookerDetails, PricingBreakdown, CouponState, AppointmentType } from '../hooks/useBookingFlow';
import type { AppointmentTaker } from '../../marketplace/types';
import type { TimeSlot } from '../services/appointmentService';

interface StepPaymentProps {
  taker: AppointmentTaker;
  appointmentType: AppointmentType;
  selectedSlot: TimeSlot;
  bookerDetails: BookerDetails;
  pricing: PricingBreakdown;
  coupon: CouponState;
  paymentStatus: string;
  paymentError: string | null;
  slug: string;
  onCouponCodeChange: (code: string) => void;
  onApplyCoupon: () => Promise<void>;
  onPayNow: (slug: string) => Promise<void>;
  onBookFree: () => Promise<void>;
}

const StepPayment: React.FC<StepPaymentProps> = ({
  taker,
  appointmentType,
  selectedSlot,
  bookerDetails,
  pricing,
  coupon,
  paymentStatus,
  paymentError,
  slug,
  onCouponCodeChange,
  onApplyCoupon,
  onPayNow,
  onBookFree,
}) => {
  const navigate = useNavigate();
  const [promoOpen, setPromoOpen] = useState(false);
  const tz = taker.timeZone || 'America/Toronto';
  const isPending = paymentStatus === 'pending';
  const isINRPaid = taker.currency === 'INR' && pricing.total > 0;
  const isFree = pricing.total === 0 || coupon.isFree;

  const slotDateStr = format(selectedSlot.start, 'EEEE, MMMM d, yyyy');
  const slotTimeStr = `${formatTimeWithZone(selectedSlot.start, 'h:mm a', tz)} – ${formatTimeWithZone(selectedSlot.end, 'h:mm a', tz)}`;

  return (
    <Box>
      <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: BOOKING_THEME.textPrimary }}>
        Step 3: Payment
      </Typography>

      {/* Appointment summary */}
      <Paper
        elevation={0}
        sx={{
          p: 2.5,
          mb: 2,
          backgroundColor: BOOKING_THEME.pageBg,
          border: `1px solid ${BOOKING_THEME.border}`,
          borderRadius: 2,
        }}
      >
        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5, color: BOOKING_THEME.textPrimary }}>
          Appointment Summary
        </Typography>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
          <Row label="Consultant" value={taker.displayName} />
          <Row label="Type" value={appointmentType === 'walk-in' ? 'Walk-in' : 'Book a call'} />
          <Row label="Date" value={slotDateStr} />
          <Row label="Time" value={slotTimeStr} />
          <Row label="Name" value={bookerDetails.name} />
          <Row label="Email" value={bookerDetails.email} />
          {appointmentType === 'walk-in' && taker.officeAddress && (
            <Row label="Location" value={taker.officeAddress.fullAddress} />
          )}
        </Box>
      </Paper>

      {/* Promo code accordion */}
      {!isINRPaid && (
        <Accordion
          expanded={promoOpen}
          onChange={(_, open) => setPromoOpen(open)}
          elevation={0}
          sx={{
            border: `1px solid ${BOOKING_THEME.border}`,
            borderRadius: '8px !important',
            mb: 2,
            '&:before': { display: 'none' },
            backgroundColor: BOOKING_THEME.promoAccordionBg,
          }}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              {coupon.applied ? `✓ Promo applied: ${coupon.code}` : 'Have a promo code?'}
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Box sx={{ display: 'flex', gap: 1, mb: coupon.error ? 0 : 1 }}>
              <TextField
                size="small"
                placeholder="Enter code"
                value={coupon.code}
                onChange={(e) => onCouponCodeChange(e.target.value)}
                disabled={coupon.applied || coupon.loading}
                sx={{ flex: 1, '& .MuiOutlinedInput-root fieldset': { borderColor: BOOKING_THEME.border } }}
              />
              <Button
                variant="outlined"
                size="small"
                onClick={onApplyCoupon}
                disabled={coupon.applied || coupon.loading || !coupon.code.trim()}
                sx={{
                  borderColor: BOOKING_THEME.border,
                  color: BOOKING_THEME.textPrimary,
                  minWidth: 80,
                }}
              >
                {coupon.loading ? <CircularProgress size={16} /> : coupon.applied ? 'Applied' : 'Apply'}
              </Button>
            </Box>
            {coupon.error && (
              <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
                {coupon.error}
              </Typography>
            )}
            {coupon.applied && (
              <Typography variant="caption" sx={{ color: '#2e7d32', display: 'block' }}>
                {coupon.isFree ? 'Full discount applied — this booking is free!' : `Discount of $${coupon.discount.toFixed(2)} applied`}
              </Typography>
            )}
          </AccordionDetails>
        </Accordion>
      )}

      {/* Price breakdown */}
      {!isINRPaid && (
        <Paper
          elevation={0}
          sx={{
            p: 2,
            mb: 2.5,
            border: `1px solid ${BOOKING_THEME.border}`,
            borderRadius: 2,
            backgroundColor: BOOKING_THEME.cardBg,
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.75 }}>
            <Typography variant="body2" color="text.secondary">Consultation fee</Typography>
            <Typography variant="body2">{pricing.currency} ${taker.fee.toFixed(2)}</Typography>
          </Box>
          {coupon.applied && coupon.discount > 0 && (
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.75 }}>
              <Typography variant="body2" color="text.secondary">Promo discount</Typography>
              <Typography variant="body2" sx={{ color: '#2e7d32' }}>− ${coupon.discount.toFixed(2)}</Typography>
            </Box>
          )}
          {pricing.hst > 0 && (
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.75 }}>
              <Typography variant="body2" color="text.secondary">HST (13%)</Typography>
              <Typography variant="body2">{pricing.currency} ${pricing.hst.toFixed(2)}</Typography>
            </Box>
          )}
          <Divider sx={{ my: 1 }} />
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="subtitle2" fontWeight={700}>Total</Typography>
            <Typography variant="subtitle2" fontWeight={700}>
              {pricing.currency} ${pricing.total.toFixed(2)}
            </Typography>
          </Box>
        </Paper>
      )}

      {/* INR blocked message */}
      {isINRPaid && (
        <Alert severity="info" sx={{ mb: 2.5 }}>
          <strong>India payment:</strong> Online payment for INR consultations is not yet available. Please contact us to book.
          <br />
          Consultation fee: {taker.currency} {taker.fee.toFixed(0)}
        </Alert>
      )}

      {/* Payment error */}
      {paymentError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {paymentError}
        </Alert>
      )}

      {/* CTA */}
      {isINRPaid ? (
        <Button
          variant="contained"
          fullWidth
          size="large"
          startIcon={<ChatBubbleOutlineIcon />}
          onClick={() => navigate('/chat')}
          sx={{ bgcolor: BOOKING_THEME.stepperActive, color: '#fff', '&:hover': { bgcolor: '#222' }, borderRadius: 2 }}
        >
          Contact us to book
        </Button>
      ) : isFree ? (
        <Button
          variant="contained"
          fullWidth
          size="large"
          startIcon={<LockIcon />}
          onClick={onBookFree}
          disabled={isPending}
          sx={{ bgcolor: BOOKING_THEME.stepperActive, color: '#fff', '&:hover': { bgcolor: '#222' }, borderRadius: 2, py: 1.5 }}
        >
          {isPending
            ? <CircularProgress size={22} sx={{ color: '#fff' }} />
            : `Pay ${pricing.currency} $${pricing.total.toFixed(2)} securely`
          }
        </Button>
      ) : (
        <Button
          variant="contained"
          fullWidth
          size="large"
          startIcon={<LockIcon />}
          onClick={() => onPayNow(slug)}
          disabled={isPending}
          sx={{ bgcolor: BOOKING_THEME.stepperActive, color: '#fff', '&:hover': { bgcolor: '#222' }, borderRadius: 2, py: 1.5 }}
        >
          {isPending
            ? <CircularProgress size={22} sx={{ color: '#fff' }} />
            : `Pay ${pricing.currency} $${pricing.total.toFixed(2)} securely`
          }
        </Button>
      )}

      {!isINRPaid && !isFree && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center', mt: 1 }}>
          You'll be redirected to Stripe for secure payment
        </Typography>
      )}
    </Box>
  );
};

const Row: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
    <Typography variant="body2" color="text.secondary" sx={{ flexShrink: 0 }}>{label}</Typography>
    <Typography variant="body2" sx={{ textAlign: 'right', fontWeight: 500 }}>{value}</Typography>
  </Box>
);

export default StepPayment;
